/* eslint-disable no-use-before-define */
/* eslint-disable react/function-component-definition */
import React, { forwardRef } from 'react';

import './Button.css';
import { Icon, IconItem } from '../icon/Icon';

export type ButtonItem = {
  ariaControls?: string;
  ariaExpanded?: boolean;
  ariaLabel?: string;
  ariaPressed?: boolean;
  className?: string;
  color?: 'pink' | 'blue' | 'dark' | 'white';
  variant?: 'primary' | 'secondary' | 'ghost' | 'icon-only';
  icon?: IconItem;
  iconPosition?: 'left' | 'right' | 'center';
  id?: string;
  isDecorative?: boolean;
  isDisabled?: boolean;
  isFullWidth?: boolean;
  children?: React.ReactNode;
  onClick?: (e?: React.SyntheticEvent | Event) => void;
  type?: 'button' | 'submit' | 'reset';
  tabIndex?: number;
  dataTestId?: string;
  formNoValidate?: boolean;
};

export const Button = forwardRef<HTMLButtonElement, ButtonItem>(function Button(
  {
    ariaControls,
    ariaExpanded,
    ariaLabel,
    ariaPressed,
    className,
    color = 'pink',
    variant = 'primary',
    icon,
    iconPosition = 'left',
    id,
    isDecorative = false,
    isDisabled = false,
    isFullWidth = false,
    tabIndex,
    type = 'button',
    children,
    onClick,
    dataTestId,
    formNoValidate,
  }: ButtonItem,
  ref: React.Ref<HTMLButtonElement>
) {
  const classes = [
    'btn',
    `btn-${variant}`,
    `btn-${color}`,
    isDecorative ? 'btn-decorative' : '',
    isDisabled ? 'btn-disabled' : '',
    isFullWidth ? 'wide' : '',
    className,
  ].join(' ');

  return (
    <button
      aria-controls={ariaControls}
      aria-expanded={ariaExpanded}
      aria-label={ariaLabel}
      aria-hidden={isDecorative}
      aria-pressed={ariaPressed}
      className={classes}
      onClick={onClick}
      disabled={isDisabled}
      type={type}
      id={id}
      tabIndex={isDecorative ? -1 : tabIndex}
      ref={ref}
      data-testid={dataTestId}
      formNoValidate={formNoValidate}
    >
      {variant !== 'icon-only' && icon && iconPosition === 'left' && (
        <ButtonIcon icon={icon} iconPosition={iconPosition} variant={variant} />
      )}
      {variant !== 'icon-only' && children}
      {variant !== 'icon-only' && icon && iconPosition === 'right' && (
        <ButtonIcon icon={icon} iconPosition={iconPosition} variant={variant} />
      )}
      {variant !== 'icon-only' && icon && iconPosition === 'center' && (
        <ButtonIcon icon={icon} iconPosition={iconPosition} variant={variant} />
      )}
      {variant === 'icon-only' && icon && (
        <ButtonIcon icon={icon} iconPosition={iconPosition} variant={variant} />
      )}
    </button>
  );
});

type ButtonIconProps = {
  icon?: IconItem;
  iconPosition?: 'left' | 'right' | 'center';
  variant?: 'primary' | 'secondary' | 'ghost' | 'icon-only';
};

const ButtonIcon = ({ iconPosition, variant, icon }: ButtonIconProps) => (
  <Icon
    className={`btn-icon-${iconPosition}${variant === 'icon-only' ? ' btn-icon-only' : ''}`}
    icon={icon}
  />
);
